import { FC, FormEvent, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Skeleton } from 'antd';
import { TermsAcceptanceApi } from 'Data/TermsAcceptance.api';
import {
  IPublicTermsAcceptanceResponse,
  TermsAcceptanceStatusEnum,
} from 'Data/interfaces/Faturamento/ITermsAcceptance';
import { TERMS_ACCEPTANCE_QUERY_KEY } from 'Data/keys/QueryKeys';
import { getApiErrorMessage } from 'Utils/ApiErrorUtils';
import { stringToCPF } from 'Utils/MaskUtils';
import {
  Card,
  Company,
  Declaration,
  ErrorText,
  Field,
  Label,
  Muted,
  Page,
  Result,
  Title,
} from './styles';

// Página pública do link de ciência nos Termos de Uso, enviado por e-mail à matriz
// (admin do tenant) e ao representante legal de cada SPE do rateio. Não exige
// login: o representante da SPE normalmente não tem conta na plataforma. Fica fora
// da divisão logado/deslogado do App (ver App.tsx) para funcionar nos dois casos.
//
// O texto da declaração (terms.page.declaration*) é um rascunho e precisa ser
// validado pelo jurídico antes de ir para produção.
const TermsAcceptance: FC = () => {
  const { t } = useTranslation('faturamento');
  const { token = '' } = useParams();

  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [accepted, setAccepted] = useState(false);
  // Resposta do aceite: substitui o que veio do GET para mostrar a confirmação.
  const [acceptResult, setAcceptResult] = useState<IPublicTermsAcceptanceResponse>();

  const { data, isLoading, error } = useQuery({
    queryKey: [TERMS_ACCEPTANCE_QUERY_KEY, token],
    queryFn: () => TermsAcceptanceApi.getByToken(token),
    retry: false,
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: () => TermsAcceptanceApi.accept(token, { name, cpf, accepted }),
    onSuccess: (result) => setAcceptResult(result),
  });

  const current = acceptResult ?? data;
  const cpfDigits = cpf.replace(/\D/g, '');
  const canSubmit = name.trim().length > 0 && cpfDigits.length === 11 && accepted;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit) acceptMutation.mutate();
  };

  const renderBody = () => {
    if (isLoading) return <Skeleton active />;

    if (error || !current) {
      return <Result>{getApiErrorMessage(error, t('terms.page.invalid'))}</Result>;
    }

    const company = current.CompanyDocument
      ? `${current.CompanyName} (${current.CompanyDocument})`
      : current.CompanyName;

    if (current.Status === TermsAcceptanceStatusEnum.Accepted) {
      return (
        <Result $ok>
          {t('terms.page.done', {
            company,
            date: current.AcceptedAtUtc
              ? new Date(current.AcceptedAtUtc).toLocaleString()
              : '',
          })}
        </Result>
      );
    }

    if (current.Status === TermsAcceptanceStatusEnum.Superseded) {
      return <Result>{t('terms.page.superseded')}</Result>;
    }
    if (current.Status === TermsAcceptanceStatusEnum.Locked) {
      return <Result>{t('terms.page.locked')}</Result>;
    }
    if (current.IsExpired) {
      return <Result>{t('terms.page.expired')}</Result>;
    }

    return (
      <form onSubmit={onSubmit}>
        <Muted>{t('terms.page.greeting', { name: current.RecipientName })}</Muted>
        <Company>{company}</Company>
        <p>
          {current.IsHeadquarters
            ? t('terms.page.introHeadquarters')
            : t('terms.page.introSpe')}
        </p>
        <p>
          <a href={current.TermsUrl} target="_blank" rel="noopener noreferrer">
            {t('terms.page.readTerms')}
          </a>{' '}
          <Muted as="span">{t('terms.page.version', { version: current.TermsVersion })}</Muted>
        </p>

        <Field>
          <Label htmlFor="terms-name">{t('terms.page.name')}</Label>
          <Input
            id="terms-name"
            value={name}
            maxLength={256}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="terms-cpf">{t('terms.page.cpf')}</Label>
          <Input
            id="terms-cpf"
            value={cpf}
            maxLength={14}
            inputMode="numeric"
            onChange={(e) => setCpf(stringToCPF(e.target.value) ?? '')}
          />
          {!current.IsHeadquarters ? <Muted>{t('terms.page.cpfHint')}</Muted> : null}
        </Field>

        <Declaration>
          <Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)}>
            {current.IsHeadquarters
              ? t('terms.page.declarationHeadquarters', { company: current.CompanyName })
              : t('terms.page.declarationSpe', { company: current.CompanyName })}
          </Checkbox>
        </Declaration>

        {acceptMutation.isError ? (
          <ErrorText role="alert">
            {getApiErrorMessage(acceptMutation.error, t('terms.page.acceptError'))}
          </ErrorText>
        ) : null}

        <Button
          type="primary"
          htmlType="submit"
          disabled={!canSubmit}
          loading={acceptMutation.isPending}
        >
          {t('terms.page.submit')}
        </Button>
      </form>
    );
  };

  return (
    <Page>
      <Card>
        <Title>{t('terms.page.title')}</Title>
        {renderBody()}
      </Card>
    </Page>
  );
};

export default TermsAcceptance;
